import { ApiError } from '@/api/client'
import {
  useExcludedRecipes,
  useMealOptions,
  usePairEvaluation,
  usePartners,
  useRecipe,
  useRecipeSearch,
  useRecordPrep,
  useRemovePrep,
  useRuleCatalog,
  useRuleState,
  useSaveRuleState,
  useSaveWeightState,
  useSetExcluded,
  useWeightState,
} from '@/api/hooks'
import type {
  MealOption,
  RecipeSummary,
} from '@/api/types'
import { Button } from '@/components/ui/Button'
import {
  EmptyState,
  ErrorNote,
  SkeletonRows,
} from '@/components/ui/Feedback'
import { useToast } from '@/components/ui/toastContext'
import { PageHeader, PageShell } from '@/components/layout/Page'
import { RecipePreviewDrawer } from '@/features/recipes/RecipePreview'
import { RulePanel } from './RulePanel'
import {
  RULE_PARAM,
  describeRule,
  hasRuleParam,
  isComplete,
  knownRules,
  parseRule,
  readRules,
  ruleParams,
  writeRules,
  type Rule,
} from './rules'
import { SelectionTray } from './SelectionTray'
import { CandidateCard } from './CandidateCard'
import { pairRows, recipeRows } from './scoreRows'
import {
  DEFAULT_TUNING,
  hasTuningParam,
  parseTuning,
  tuningFromWeights,
  type Tuning,
  type TuningKey,
} from './tuning'
import { WeightPanel } from './WeightPanel'
import { formatScore, relativeFromNow } from '@/lib/format'
import { useDebounced } from '@/lib/useDebounced'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import {
  Check,
  ChevronDown,
  Sparkles,
  Undo2,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

/** Results per page, shared by both workflow steps. */
const PAGE = 5

const EMPTY_VIOLATIONS: string[] = []

function parseId(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

/**
 * The single rule set: what the URL asks for, what the request should send, and
 * whether both can be decided yet.
 *
 * A URL that says nothing about the rules falls back to what was remembered,
 * and then to the server's defaults. The catalogue is needed to tell a real
 * rule from a mistyped one, and the remembered set arrives separately, so the
 * request waits for both rather than asking for the defaults first.
 */
function useWorkflowRules(params: URLSearchParams) {
  const catalog = useRuleCatalog()
  const state = useRuleState()
  const remembered = state.data?.rules

  const rules = useMemo(() => {
    if (hasRuleParam(params)) return readRules(params, catalog.data)
    if (remembered) return knownRules(remembered, catalog.data)
    return readRules(params, catalog.data)
  }, [params, catalog.data, remembered])

  const requestRules = useMemo(() => {
    if (hasRuleParam(params)) return ruleParams(params, catalog.data)
    if (remembered) {
      const restored = knownRules(remembered, catalog.data)
      return restored.length > 0 ? writeRules(restored) : ['']
    }
    return []
  }, [params, catalog.data, remembered])

  const known = !catalog.isPending && (hasRuleParam(params) || !state.isPending)
  return { rules, requestRules, known, catalog: catalog.data }
}

export function RecommendationsPage() {
  useDocumentTitle('Recommend')
  const [params, setParams] = useSearchParams()
  const meal1Id = parseId(params.get('meal1'))
  const meal2Id = parseId(params.get('meal2'))
  const step = params.get('step') === '2' && meal1Id !== null

  // A URL that names any weight wins; otherwise the remembered set, otherwise
  // the defaults. The request waits for the remembered set so a tuned account
  // never sees a flash of default rankings first.
  const weightState = useWeightState()
  const weightsKnown = !weightState.isPending
  const tuning = useMemo(
    () =>
      hasTuningParam(params)
        ? parseTuning(params)
        : tuningFromWeights(weightState.data?.weights),
    [params, weightState.data],
  )
  const [openTuningPanel, setOpenTuningPanel] = useState<'rules' | 'weights' | null>(null)

  const workflow = useWorkflowRules(params)
  const rules = workflow.rules
  const catalog = workflow.catalog
  const [limit, setLimit] = useState(PAGE)

  function updateRules(next: Rule[]) {
    const merged = new URLSearchParams(params)
    merged.delete(RULE_PARAM)
    // An empty rule parameter is how "no rules at all" is said. Without it the
    // server reads the request as "use the defaults".
    if (next.length === 0) merged.append(RULE_PARAM, '')
    else for (const rule of writeRules(next)) merged.append(RULE_PARAM, rule)
    setParams(merged, { replace: true })
    setLimit(PAGE)
  }

  // Remember the rules for the next visit. Debounced: editing a rule writes to
  // the URL on every keystroke, and only the last is worth keeping.
  const { mutate: saveRules } = useSaveRuleState()
  useEffect(() => {
    if (!hasRuleParam(params) || !catalog) return
    const timer = window.setTimeout(() => {
      saveRules({
        rules: writeRules(rules.filter((rule) => isComplete(rule, catalog))),
      })
    }, 500)
    return () => window.clearTimeout(timer)
  }, [rules, params, catalog, saveRules])

  // Remember weights whenever the URL names them, which includes a preset load
  // and an explicit reset to the defaults. Debounced like the rules.
  const { mutate: saveWeights } = useSaveWeightState()
  useEffect(() => {
    if (!hasTuningParam(params)) return
    const timer = window.setTimeout(() => {
      saveWeights({ weights: parseTuning(params) })
    }, 500)
    return () => window.clearTimeout(timer)
  }, [params, saveWeights])

  const toast = useToast()
  const mealOptions = useMealOptions(
    limit,
    tuning,
    workflow.requestRules,
    workflow.known && weightsKnown,
  )
  // The partner query also carries the violations for meal 1, so it is useful
  // on step 1 when a meal was typed by hand.
  const partners = usePartners(
    meal1Id,
    limit,
    tuning,
    workflow.requestRules,
    workflow.known && weightsKnown,
  )
  const evaluation = usePairEvaluation(
    meal1Id,
    step ? meal2Id : null,
    tuning,
    workflow.requestRules,
    workflow.known && weightsKnown,
  )

  const [meal1Term, setMeal1Term] = useState('')
  const debouncedMeal1 = useDebounced(meal1Term)
  const meal1Search = useRecipeSearch(debouncedMeal1)

  const [meal2Term, setMeal2Term] = useState('')
  const debouncedMeal2 = useDebounced(meal2Term)
  const meal2Search = useRecipeSearch(debouncedMeal2, { enabled: step })

  const meal1Detail = useRecipe(meal1Id)
  const meal2Detail = useRecipe(step ? meal2Id : null)
  const recordPrep = useRecordPrep()
  const removePrep = useRemovePrep()
  const [peekId, setPeekId] = useState<number | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)

  // On a phone the option list and the partner list share one viewport. Moving
  // to step 2 while deep in the options would otherwise leave the user halfway
  // down a list they have not seen yet.
  useEffect(() => {
    if (!step || !window.matchMedia('(max-width: 1023px)').matches) return
    window.scrollTo({ top: 0 })
  }, [step])

  function changeParams(
    changes: Record<string, string | null>,
    replace = false,
  ) {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key)
      else next.set(key, value)
    }
    setParams(next, { replace })
  }

  function selectMeal1(id: number) {
    if (id === meal1Id) {
      changeParams({ step: '2' })
      return
    }
    changeParams({ meal1: String(id), meal2: null, step: '2' })
    setLimit(PAGE)
  }

  function clearMeal1() {
    changeParams({ meal1: null, meal2: null, step: null }, true)
    setLimit(PAGE)
  }

  function selectMeal2(id: number) {
    changeParams({ meal2: String(id), step: '2' })
  }

  function clearMeal2() {
    changeParams({ meal2: null }, true)
  }

  function updateTuning(next: Tuning) {
    // Every weight is written, defaults included. A partial URL would fall back
    // to the remembered set, which is exactly what "reset to defaults" must not
    // do, and a loaded preset would otherwise be only partly represented.
    const merged = new URLSearchParams(params)
    for (const key of Object.keys(DEFAULT_TUNING) as TuningKey[]) {
      merged.set(key, String(next[key]))
    }
    setParams(merged, { replace: true })
  }

  function showMore() {
    setLimit((current) => current + PAGE)
  }

  function refetchAll() {
    void mealOptions.refetch()
    void partners.refetch()
    void evaluation.refetch()
  }

  async function cook(recipeIds: number[], label: string, key: string) {
    if (chosen === key) return
    setChosen(key)
    try {
      const result = await recordPrep.mutateAsync(Array.from(new Set(recipeIds)))
      // Only `changed` was actually recorded. A recipe already on record for
      // today is left alone, so it must not be part of the undo either.
      const recorded = result.changed
      toast.success(
        recorded.length === 0
          ? `${label} already recorded as prepared today.`
          : `${label} marked as prepared.`,
        recorded.length === 0
          ? undefined
          : { action: { label: 'Undo', onClick: () => void undoChoice(recorded) } },
      )
      refetchAll()
    } catch (error) {
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not record the preparation.',
      )
    } finally {
      setChosen(null)
    }
  }

  /**
   * Take back a misclick on "cook". Only the recipes this click actually
   * recorded are passed on: a combination can include one that was already
   * logged today, and removing that would lose a real preparation.
   */
  async function undoChoice(recordedIds: number[]) {
    try {
      await removePrep.mutateAsync({ recipeIds: recordedIds })
      toast.success('Preparation removed.')
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not undo that.',
      )
    }
    refetchAll()
  }

  const meal1Name = meal1Detail.isError
    ? 'Recipe unavailable'
    : (meal1Detail.data?.recipe.name ?? undefined)
  const meal2Name = meal2Detail.isError
    ? 'Recipe unavailable'
    : (meal2Detail.data?.recipe.name ?? undefined)

  const meal1Options = useMemo(
    () => optionsForSearch(meal1Search.data?.items ?? []),
    [meal1Search.data],
  )
  const meal2Options = useMemo(
    () => optionsForSearch(meal2Search.data?.items ?? []),
    [meal2Search.data],
  )

  function describeViolations(raw: string[]): string[] {
    const described = raw.map((text) => {
      const rule = parseRule(text)
      if (!rule || !catalog) return text
      return describeRule(rule, catalog) ?? text
    })
    // The same rule can be broken by both meals, and it should be named once.
    return Array.from(new Set(described))
  }

  const step1Violations = describeViolations(
    partners.data?.meal1_violations ?? EMPTY_VIOLATIONS,
  )
  const evaluationViolations = evaluation.data
    ? describeViolations([
        ...evaluation.data.meal1_violations,
        ...evaluation.data.meal2_violations,
        ...evaluation.data.pair_violations,
      ])
    : EMPTY_VIOLATIONS

  // Before the evaluation lands, the partner response already knows which pool
  // rules meal 1 breaks, so the banner does not wait for a second request.
  const warningLines = step
    ? evaluation.data
      ? evaluationViolations
      : step1Violations
    : step1Violations
  const meal1Option = mealOptions.data?.meal_options.find(
    (option) => option.recipe.id === meal1Id,
  )
  const meal1Recipe = partners.data?.selected_recipe ?? meal1Option?.recipe
  const selectedPartner = partners.data?.partners.find(
    (partner) => partner.recipe.id === meal2Id,
  )
  const meal2Recipe = evaluation.data?.meal2 ?? selectedPartner?.recipe

  // Unknown until the request that carries the checks has landed.
  const warningsKnown =
    meal1Id !== null && (step && meal2Id !== null ? evaluation.data : partners.data)
  const trayWarnings = warningsKnown ? warningLines : null

  const tray = useRef<HTMLElement>(null)
  const [trayVisible, setTrayVisible] = useState(true)
  useEffect(() => {
    const node = tray.current
    if (!node) return
    const observer = new IntersectionObserver(([entry]) => setTrayVisible(entry?.isIntersecting ?? true))
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  function cookOne() {
    if (meal1Id === null) return
    void cook([meal1Id], meal1Name ?? 'Meal 1', 'meal-1')
  }
  function cookBoth() {
    if (meal1Id === null || meal2Id === null) return
    void cook([meal1Id, meal2Id], 'Both meals', 'both')
  }

  const showPartners = step && meal1Id !== null

  return (
    <PageShell className="pb-32">
      <PageHeader title="Recommend" subtitle="Pick two meals for the week, or just one." />

      <div className="space-y-6">
        <div className="grid grid-cols-[auto_auto_1fr] items-start gap-x-2 gap-y-2">
          <RulePanel
            className="contents"
            rules={rules}
            onChange={updateRules}
            open={openTuningPanel === 'rules'}
            onOpenChange={(open) => setOpenTuningPanel(open ? 'rules' : null)}
          />
          <WeightPanel
            className="contents"
            tuning={tuning}
            onChange={updateTuning}
            open={openTuningPanel === 'weights'}
            onOpenChange={(open) => setOpenTuningPanel(open ? 'weights' : null)}
          />
        </div>

        <SelectionTray
          ref={tray}
          meal1={
            meal1Id === null
              ? null
              : {
                  id: meal1Id,
                  name: meal1Name ?? meal1Recipe?.name ?? 'Meal 1',
                  recipe: meal1Recipe,
                  score: meal1Option
                    ? {
                        value: meal1Option.score,
                        rows: recipeRows(meal1Option.score_details, meal1Option.recipe.name),
                        label: 'Recipe score',
                      }
                    : undefined,
                }
          }
          meal2={
            step && meal2Id !== null
              ? { id: meal2Id, name: meal2Name ?? meal2Recipe?.name ?? 'Meal 2', recipe: meal2Recipe }
              : null
          }
          search1={{
            options: meal1Options,
            onSearch: setMeal1Term,
            onPick: selectMeal1,
            loading: meal1Search.isFetching,
          }}
          search2={{
            options: meal2Options,
            onSearch: setMeal2Term,
            onPick: selectMeal2,
            loading: meal2Search.isFetching,
          }}
          onPreview={setPeekId}
          onChangeMeal1={() => changeParams({ step: null, meal2: null })}
          onRemoveMeal1={clearMeal1}
          onRemoveMeal2={clearMeal2}
          pairScore={
            evaluation.data
              ? {
                  value: evaluation.data.score,
                  rows: pairRows(
                    evaluation.data.score_details,
                    evaluation.data.meal1.name,
                    evaluation.data.meal2.name,
                  ),
                }
              : undefined
          }
          warnings={trayWarnings}
          chosen={chosen}
          onCookOne={cookOne}
          onCookBoth={cookBoth}
        />

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-ink">
            {showPartners
              ? `Partners for ${meal1Name ?? 'meal 1'}`
              : 'Suggestions for meal 1'}
          </h2>
          {showPartners ? (
            <PartnersList
              query={partners}
              limit={limit}
              selectedId={meal2Id}
              onSelect={selectMeal2}
              onPeek={setPeekId}
              onMore={showMore}
            />
          ) : (
            <StepOneOptions
              query={mealOptions}
              rules={rules}
              limit={limit}
              selectedId={meal1Id}
              onSelect={selectMeal1}
              onPeek={setPeekId}
              onMore={showMore}
            />
          )}
        </section>

        <ExcludedPanel />
      </div>

      {meal1Id !== null && !trayVisible ? (
        <div className="fixed inset-x-0 bottom-16 z-30 border-t border-line bg-surface/95 shadow-raised backdrop-blur-md md:bottom-0 md:left-60">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-ink">
                {meal1Name ?? 'Meal 1'}
                {step && meal2Id !== null ? ` + ${meal2Name ?? 'Meal 2'}` : ''}
              </p>
              {trayWarnings && trayWarnings.length > 0 ? (
                <p className="truncate text-xs text-caution">
                  Breaks {trayWarnings.length === 1 ? '1 rule' : `${trayWarnings.length} rules`}
                </p>
              ) : evaluation.data ? (
                <p className="tabular text-xs text-ink-faint">
                  Pair score {formatScore(evaluation.data.score)}
                </p>
              ) : null}
            </div>
            {step && meal2Id !== null ? (
              <Button variant="primary" loading={chosen === 'both'} onClick={cookBoth}>
                <Check size={16} aria-hidden />
                Cook both
              </Button>
            ) : (
              <Button variant="primary" loading={chosen === 'meal-1'} onClick={cookOne}>
                Cook meal 1
              </Button>
            )}
          </div>
        </div>
      ) : null}

      <RecipePreviewDrawer
        recipeId={peekId}
        onClose={() => setPeekId(null)}
        actions={(id) => {
          const forMeal2 = showPartners && id !== meal1Id
          const picked = forMeal2 ? id === meal2Id : id === meal1Id
          return (
            <Button
              variant="primary"
              disabled={picked}
              onClick={() => {
                setPeekId(null)
                if (forMeal2) selectMeal2(id)
                else selectMeal1(id)
              }}
            >
              <Check size={16} aria-hidden />
              {picked ? 'Picked' : forMeal2 ? 'Use as meal 2' : 'Use as meal 1'}
            </Button>
          )
        }}
      />
    </PageShell>
  )
}

function optionsForSearch(recipes: RecipeSummary[]) {
  return recipes.map((recipe) => ({
    value: String(recipe.id),
    label: recipe.name,
    hint: [
      recipe.vegetarian ? 'vegetarian' : null,
      recipe.cuisine,
      recipe.last_prep
        ? `last made ${relativeFromNow(recipe.last_prep)}`
        : 'never made',
    ]
      .filter(Boolean)
      .join(' · '),
  }))
}

function StepOneOptions({
  query,
  rules,
  limit,
  selectedId,
  onSelect,
  onPeek,
  onMore,
}: {
  query: ReturnType<typeof useMealOptions>
  rules: Rule[]
  limit: number
  selectedId: number | null
  onSelect: (id: number) => void
  onPeek: (id: number) => void
  onMore: () => void
}) {
  if (query.isPending) return <SkeletonRows rows={4} />
  if (query.isError) {
    return (
      <ErrorNote
        body={query.error instanceof ApiError ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
      />
    )
  }

  const options = query.data?.meal_options ?? []
  if (options.length === 0) {
    return (
      <EmptyState
        icon={<Sparkles size={22} />}
        title="Nothing to recommend"
        body={narrowedBy(rules)}
      />
    )
  }

  return (
    <div className="space-y-4">
      <ol className="space-y-2">
        {options.map((option, index) => (
          <li key={option.recipe.id}>
            <CandidateCard
              recipe={option.recipe}
              rank={index + 1}
              score={option.score}
              rows={recipeRows(option.score_details, option.recipe.name)}
              scoreLabel="Recipe score"
              selected={option.recipe.id === selectedId}
              onPick={() => onSelect(option.recipe.id)}
              onPreview={() => onPeek(option.recipe.id)}
              footnote={outlookText(option)}
            />
          </li>
        ))}
      </ol>
      <ShowMore
        shown={options.length}
        limit={limit}
        loading={query.isPlaceholderData}
        onMore={onMore}
      />
    </div>
  )
}

function PartnersList({
  query,
  limit,
  selectedId,
  onSelect,
  onPeek,
  onMore,
}: {
  query: ReturnType<typeof usePartners>
  limit: number
  selectedId: number | null
  onSelect: (id: number) => void
  onPeek: (id: number) => void
  onMore: () => void
}) {
  if (query.isPending) return <SkeletonRows rows={4} />
  if (query.isError) {
    return (
      <ErrorNote
        body={query.error instanceof ApiError ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
      />
    )
  }

  const partners = query.data?.partners ?? []
  if (partners.length === 0) {
    return (
      <EmptyState
        icon={<Sparkles size={22} />}
        title="No partners found"
        body="Nothing pairs with meal 1 under your current rules. Try loosening a rule."
      />
    )
  }

  return (
    <div className="space-y-4">
      <ol className="space-y-2">
        {partners.map((partner, index) => (
          <li key={partner.recipe.id}>
            <CandidateCard
              recipe={partner.recipe}
              rank={index + 1}
              score={partner.score}
              rows={pairRows(
                partner.score_details,
                query.data?.selected_recipe.name ?? 'Meal 1',
                partner.recipe.name,
              )}
              scoreLabel="Pair score"
              selected={partner.recipe.id === selectedId}
              onPick={() => onSelect(partner.recipe.id)}
              onPreview={() => onPeek(partner.recipe.id)}
            />
          </li>
        ))}
      </ol>
      <ShowMore
        shown={partners.length}
        limit={limit}
        loading={query.isPlaceholderData}
        onMore={onMore}
      />
    </div>
  )
}

/**
 * Grows the requested page when there is more to show. A page shorter than the
 * request means the server had nothing left, so the button goes away rather than
 * sitting there doing nothing.
 */
function ShowMore({
  shown,
  limit,
  loading,
  onMore,
}: {
  shown: number
  limit: number
  loading: boolean
  onMore: () => void
}) {
  if (shown === 0) return null
  if (shown < limit && !loading) return null
  return (
    <div className="flex justify-center pt-1">
      <Button variant="secondary" loading={loading} onClick={onMore}>
        <ChevronDown size={14} aria-hidden />
        Show {PAGE} more
      </Button>
    </div>
  )
}

/**
 * What to do about an empty result. The rules are the thing most likely to have
 * over-narrowed the search, and they are one click away, so the empty state
 * points at them rather than describing a cause it cannot know.
 */
function outlookText(option: MealOption): string {
  const outlook = option.pairing_outlook
  if (outlook.partner_count === 0 || !outlook.best_partner || outlook.best_score === null) {
    return 'No partners under your rules'
  }
  return `Best with ${outlook.best_partner.name} (${formatScore(outlook.best_score)}) · ${outlook.partner_count} ${
    outlook.partner_count === 1 ? 'partner' : 'partners'
  }`
}

function narrowedBy(rules: Rule[]): string {
  if (rules.length === 0) {
    return 'Nothing matched. Every recipe is excluded or was prepared too recently.'
  }
  const cappedByRecency = rules.some(
    (rule) => rule.field === 'last_prep' && rule.scope === 'each',
  )
  const advice = cappedByRecency
    ? 'Shorten the recency rule, or wait a little longer.'
    : 'Try loosening or removing a rule.'
  return `Nothing matched the ${rules.length === 1 ? 'rule' : `${rules.length} rules`} you have set. ${advice}`
}

/** Excluded recipes, kept reachable so they can be brought back. */
function ExcludedPanel() {
  const excluded = useExcludedRecipes()
  const setExcluded = useSetExcluded()
  const toast = useToast()
  const items = excluded.data ?? []

  if (items.length === 0) return null

  return (
    <details className="group rounded-[var(--radius-card)] border border-line/70 bg-surface shadow-card">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm">
        <span className="font-medium text-ink">
          {items.length === 1 ? '1 excluded recipe' : `${items.length} excluded recipes`}
          <span className="ml-2 font-normal text-ink-faint">never suggested</span>
        </span>
        <ChevronDown size={16} aria-hidden className="text-ink-faint transition-transform group-open:rotate-180" />
      </summary>
      <div className="space-y-1 border-t border-line p-2">
        {items.map((item) => (
          <div
            key={item.recipe_id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-field)] px-2.5 py-1.5 hover:bg-surface-sunken"
          >
            <div className="min-w-0">
              <Link
                to={`/recipes/${item.recipe_id}`}
                className="truncate text-sm font-medium text-ink hover:text-accent"
              >
                {item.name}
              </Link>
              {item.reason ? (
                <p className="truncate text-micro text-ink-faint">{item.reason}</p>
              ) : null}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  await setExcluded.mutateAsync({
                    id: item.recipe_id,
                    exclude: false,
                  })
                  toast.success(`${item.name} is back in recommendations.`)
                } catch (error) {
                  toast.error(
                    error instanceof ApiError
                      ? error.message
                      : 'Could not update.',
                  )
                }
              }}
            >
              <Undo2 size={13} aria-hidden />
              Include again
            </Button>
          </div>
        ))}
      </div>
    </details>
  )
}
